import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { addMilliseconds } from 'date-fns';
import styles from './CreateScreen.module.scss';

import ControlledFormInput from '../../../common/components/ControlledFormInput';
import DatePicker from '../../../common/components/Datepicker';
import InputFieldErrorMessage from '../../../common/components/InputFieldErrorMessage';
import PageHeading from '../../../common/components/PageHeading';
import SubSection from '../../../common/components/SubSection';
import LoadingScreen from '../../../common/LoadingScreen';

import { useFormControls } from '../../../common/hooks/useFormControls';
import CreateScreenSubmissionButtons from '../components/CreateScreenSubmissionButtons';
import EventListingsEventLocationInputs from '../components/EventListingsEventLocationInputs';
import EventListingsFormControls, { requiredField } from '../forms/EventListingsForm';
import EventListingsPhotoUploader from '../components/EventListingsPhotoUploader';
import EventListingsPublishDateField from '../components/EventListingsPublishDateField';
import EventListingsQuickTips from '../components/EventListingsQuickTips/EventListingsQuickTips';
import EventListingsTermsField from '../components/EventListingsTermsField';

import EventListingService from '../services/EventListingService';

import { eventListingsURL } from '../constants';
import { scrollToTopOrError } from '../../../common/helpers/scrollTo';
import { getNowInEasternTimeLocal, calculateMinTimeOffsetInMilliseconds, getTodayAtLocalMidnight } from '../../../pressrelease/editor/helpers/timeHelpers';
import EventTypeFields from '../components/EventTypeFields/EventTypeFields';

const { eventTypes, status, site } = window.eventListings;
const isPaid = status === 'PAID';

// QUICK TIPS BULLETS //
const eventVerbiageTips = [
  { text: "Tailor your message to maximize your event's success", key: 1 },
  { text: 'We see more of our readers click on short, concise headlines. Try using a maximum of 10 to 12 words', key: 2 },
];
const eventPhotoTips = [
  {
    text: 'For best results, upload an image with a 16:9 aspect ratio (e.g., 1920x1080 pixels). This ensures your image displays correctly on our site.',
    key: 1,
  },
];

const CreateScreen = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  // eslint-disable-next-line object-curly-newline
  const { globalState: state, setGlobalState, setSaveSuccess, pushEvent } = useOutletContext();
  const { eventListingPurchase: eventObj } = state ?? {};
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    fields,
    getFormValues,
    setError,
    setErrors,
    setField,
    setFieldValue,
    resetField,
    updateForm,
    validateField,
    setValidators,
    getErroringFieldNames,
    setFieldWithError,
  } = useFormControls(EventListingsFormControls);

  const setLocationFieldValidators = (value) => {
    const updateFields = ['addressLine1', 'city', 'country'];
    if (value === 'ONLINE') {
      updateFields.forEach((fieldName) => setValidators(fieldName, []));
      // remove errors for fields that are no longer required
      updateFields.forEach((fieldName) => setError(fieldName));
    } else if (value === 'IN_PERSON') {
      updateFields.forEach((fieldName) => setValidators(fieldName, [requiredField]));
    }
  };

  useEffect(() => {
    if (eventObj) {
      updateForm(eventObj);
      setLocationFieldValidators(eventObj.locationType);
    }
  }, [eventObj]);

  const api = new EventListingService(token);

  const handleLocationTypeChange = (e) => {
    const { value } = e.target;
    setField({
      target: {
        name: 'locationType',
        value,
      },
    });
    setLocationFieldValidators(value);
  };

  const setFieldAndError = (fieldName, value, error) => {
    setFieldWithError({
      target: {
        name: fieldName,
        value,
      },
    }, error);
  };

  const validateFieldValue = (fieldName, value) => {
    validateField({
      target: {
        name: fieldName,
        value,
      },
    });
  };

  const clearInputIfDateInPast = (selectedDate, minDate, fieldName, instance) => {
    // If a past date was selected, clear the datetime selection
    // set the field value to empty string, and run validators to generate errors
    if (selectedDate < minDate) {
      instance.clear();
      instance.close();
      validateFieldValue(fieldName, '');
      return true;
    }
    return false;
  };

  const invalidPastDate = (fieldName, selectedDate, instance) => {
    const dateFields = ['publishDate', 'endDate'];
    const dateTimeFields = ['startDateTime'];
    let invalidDate = null;
    if (dateFields.includes(fieldName)) {
      invalidDate = clearInputIfDateInPast(
        selectedDate,
        getTodayAtLocalMidnight(),
        fieldName,
        instance,
      );
    } else if (dateTimeFields.includes(fieldName)) {
      invalidDate = clearInputIfDateInPast(
        selectedDate,
        getNowInEasternTimeLocal(),
        fieldName,
        instance,
      );
    }
    return invalidDate;
  };

  // VALIDATION FUNCTIONS FOR FLATPICKR FIELD INPUTS //

  const validateEventStartEndDate = (fieldName, startDateStr, endDateStr) => {
    if (startDateStr && endDateStr) {
      const errorMsg = 'Event end date must be after event start date (or the same date).';
      const startDate = new Date(startDateStr);
      // Set the end date time to the last second of the end date for easier comparison
      const endDate = new Date(`${endDateStr}T23:59:59`);
      if (endDate < startDate) {
        if (fieldName === 'endDate') { // If endDate is validating itself, set its field value with error
          setFieldAndError(fieldName, endDateStr, errorMsg);
          return true;
        } // Otherwise use the previous endDate field value
        // when changing another field adds an error to endDate
        setError('endDate', errorMsg);
        return true;
      }
      // Remove endDate error
      setError('endDate');
    }
    return false;
  };

  const validatePublishAndEndDate = (fieldName, publishDateStr, endDateStr) => {
    if (publishDateStr && endDateStr) {
      const errorMsg = 'Event end date cannot be more than one year after publish date.';
      const publishDate = new Date(publishDateStr);
      const endDate = new Date(endDateStr);

      // Get time difference in milliseconds
      const timeDifference = endDate - publishDate;
      // convert time difference into days (divide by # of ms in a day)
      const dayDifference = timeDifference / (1000 * 60 * 60 * 24);
      if (dayDifference > 365) {
        if (fieldName === 'endDate') { // If endDate is validating itself, set its field value with error
          setFieldAndError(fieldName, endDateStr, errorMsg);
          return true;
        } // Otherwise use the previous endDate field value
        // when changing another field adds an error to endDate
        setError('endDate', errorMsg);
        return true;
      }
      // Remove endDate error
      setError('endDate');
    }
    return false;
  };

  const validatePublishAndStartDate = (fieldName, publishDateStr, startDateStr) => {
    // If there is no publishDate but there is a startDateTime, remove any previous error
    // from the publishDate field. Used during publishDate cross-validation that occurs
    // on startDate change.
    if (!publishDateStr && !!startDateStr && fields.publishDate.errors.length !== 0) {
      setError('publishDate');
    }
    if (publishDateStr && startDateStr) {
      const errorMsg = 'Event publish date must be before event start date (or the same date).';
      const publishDate = new Date(`${publishDateStr}T00:00:00`);
      const startDate = new Date(startDateStr);

      if (startDate < publishDate) {
        if (fieldName === 'publishDate') { // If publishDate is validating itself, set its field value with error
          setFieldAndError(fieldName, publishDateStr, errorMsg);
          return true;
        } // Otherwise use the previous publishDate field value
        // when changing another field adds an error to publishDate
        setError('publishDate', errorMsg);
        return true;
      }
      // Remove publishDate error
      setError('publishDate');
    }
    return false;
  };

  /**
   * ONCHANGE FUNCTIONS FOR FLATPICKR FIELD INPUTS
   *
   * Note: each onChange ends by calling the validateFieldValue method
   * from useFormControls. The onChange methods return early if an error is
   * being set on the field that triggered the onChange. This is because the
   * validateFieldValue method overwrites any existing field errors with
   * any errors generated by the field's useFormControls validators.
   *
   * The validation functions handle the cleanup of any manually set errors
   * that resulted from cross-field validation.
   */
  const handleStartDateTimeChange = (
    fieldName,
    endDateStr,
    publishDateStr,
  ) => (selectedDates, dateStr, instance) => {
    // If selected date was in the past, set field to blank,
    // run useFormControl validators on field, and bail
    if (invalidPastDate(fieldName, selectedDates[0], instance)) return;
    // Set the value of the field that was changed, so validations can occur
    setFieldValue(fieldName, dateStr);

    // Perform cross-field validations
    validatePublishAndStartDate(fieldName, publishDateStr, dateStr);
    // If an error is set on startDateTime, return early to avoid overwriting the set error
    if (validateEventStartEndDate(fieldName, dateStr, endDateStr)) return;
    validateFieldValue(fieldName, dateStr);
  };

  const handleEndDateChange = (
    fieldName,
    startDateStr,
    publishDateStr,
  ) => (selectedDates, dateStr, instance) => {
    // If selected date was in the past, set field to blank,
    // run useFormControl validators on field, and bail
    if (invalidPastDate(fieldName, selectedDates[0], instance)) return;
    // Set the value of the field that was changed, so validations can occur
    setFieldValue(fieldName, dateStr);

    // Perform cross-field validations
    validatePublishAndStartDate(fieldName, publishDateStr, startDateStr);
    // If an error is set on endDate, return early to avoid overwriting the set error
    if (validateEventStartEndDate(fieldName, startDateStr, dateStr)) return;
    if (validatePublishAndEndDate(fieldName, publishDateStr, dateStr)) return;
    validateFieldValue(fieldName, dateStr);
  };

  const handlePublishDateChange = (
    fieldName,
    startDateStr,
    endDateStr,
  ) => (selectedDates, dateStr, instance) => {
    // If selected date was in the past, set field to blank,
    // run useFormControl validators on field, and bail
    if (invalidPastDate(fieldName, selectedDates[0], instance)) return;

    // The publishDate field is validated against both startDateTime and endDate,
    // so if those fields are already invalid, clear whatever the user selects
    // for publishDate and alert them to fix the above error
    if (validateEventStartEndDate(fieldName, startDateStr, endDateStr)) {
      if (!dateStr) { // If publishDate was cleared in a previous validation,
        setError(fieldName); // remove any existing publishDate error
        return;
      }
      instance.clear();
      // The following error that is set is removed when validatePublishAndStartDate is
      // called during handleStartDateTimeChange
      setFieldAndError(fieldName, '', 'Adjust event end date before selecting publish date');
      return;
    }

    // Set the value of the field that was changed, so validations can occur
    setFieldValue(fieldName, dateStr);

    // Perform cross-field validations
    validatePublishAndEndDate(fieldName, dateStr, endDateStr);
    // If an error is set on publishDate, return early to avoid overwriting the set error
    if (validatePublishAndStartDate(fieldName, dateStr, startDateStr)) return;
    validateFieldValue(fieldName, dateStr);
  };

  const handleDatepickerReady = (fieldName) => (selectedDates, dateStr, instance) => {
    if (dateStr) invalidPastDate(fieldName, selectedDates[0], instance);
  };

  const nowInETLocal = getNowInEasternTimeLocal();
  // The # of ms until a 5-minute interval of the hour at least 5 minutes from now
  // E.g., if it's 9:03, the next available time is 9:10
  const minTimeOffsetInMilliseconds = calculateMinTimeOffsetInMilliseconds(nowInETLocal, 5);

  // CONFIG OPTIONS FOR DATE-ONLY FLATPICKR FIELDS //
  const dateOnlyConfig = {
    // format the date that gets written into the input (that will be sent in POST)
    dateFormat: 'Y-m-d',
    // disallow time input
    enableTime: false,
    // For post-selection display
    altInput: true,
    altFormat: 'm / d / Y',
  };

  /**
   * Handles the form submission for creating or updating an event listing.
   *
   * @param {Event} e - The form submission event.
   * @returns {Object|null>} - the response data if the submission is successful,
   *                            or null if there is an error.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const erroringFieldNames = getErroringFieldNames();
    if (erroringFieldNames.length === 0) {
      const formValues = getFormValues();
      formValues.eventId = formValues.eventId ?? 0;
      try {
        const data = await api.updateEventListing(token, formValues);
        // send GTM event only when we are not in the edit flow
        if (!isPaid) pushEvent('createScreenSubmit');
        return data;
      } catch (error) {
        if (error?.response?.status === 400) {
          setErrors(error?.response.data);
          scrollToTopOrError(Object.keys(error?.response.data));
        }
      }
    }
    scrollToTopOrError(erroringFieldNames);
    return null;
  };

  const handleSubmitEffects = async (e, successCallback) => {
    setIsSubmitting(true);
    const data = await handleSubmit(e);
    setIsSubmitting(false);
    if (data) {
      successCallback(data);
    }
  };

  const handleCheckout = async (e) => {
    await handleSubmitEffects(e, (data) => {
      setGlobalState(data);
      const eventStatus = data.eventListingPurchase.status;
      switch (eventStatus) {
        case 'PAID':
          scrollToTopOrError();
          setSaveSuccess(true);
          break;
        case 'PENDING':
          navigate(`${eventListingsURL}/${data.eventListingPurchase.purchaseToken}/checkout`);
          scrollToTopOrError();
          break;
        default:
          break;
      }
    });
  };

  const handlePreview = async (e) => {
    await handleSubmitEffects(e, (data) => {
      window.open(data?.eventListingPurchase?.absolutePreviewUrlWithToken, '_blank');
      // send GTM event
      pushEvent('listingPreview');
    });
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.deleteEventListing(token);
      setIsSubmitting(false);
      window.location.href = '/events/?deleteSuccess=1';
    } catch (error) {
      setError('terms', 'Failed to delete event listing');
      setIsSubmitting(false);
      scrollToTopOrError();
    }
  };

  const isDirty = Object.keys(fields).some(
    (fieldName) => { return fieldName === 'terms' ? false : fields[fieldName].dirty; },
  );
  const isValid = Object.keys(fields).every((fieldName) => fields[fieldName].valid);
  const isSubmittable = !isPaid || (isDirty && isValid);

  const pageDescription = isPaid
    ? 'Make changes to a published event. If the event has been canceled with no new date, delete the event to remove it.'
    : `Fill in event details and drive attendees in front of ${site.name}'${site.name.endsWith('s') ? '' : 's'} audience.`;

  return (
    <>
      <main id="skip-link-target">
        { isSubmitting && <LoadingScreen />}
        <PageHeading
          title={isPaid ? 'Manage Event Listing' : 'Create an Event'}
          description={pageDescription}
        />

        {isPaid && (
          <div className={styles.topEditButtons}>
            <CreateScreenSubmissionButtons
              handleCheckout={handleCheckout}
              handleDelete={handleDelete}
              handlePreview={handlePreview}
              status={status}
              touched={isSubmittable}
            />
          </div>
        )}

        <SubSection
          title="Tell us about your event"
          variant="stretch"
        >
          <div className="row">
            <div className={`columns small-12 large-4 large-push-8 ${styles['event-message-tips']}`}>
              <EventListingsQuickTips topMargin tips={eventVerbiageTips} />
            </div>
            <div className="columns small-12 large-8 large-pull-4 end">
              <ControlledFormInput
                field={fields.title}
                fieldName="title"
                onChange={setField}
                onBlur={validateField}
                maxLength={100}
                characterCounter
                required
              />
              <ControlledFormInput
                field={fields.teaser}
                fieldName="teaser"
                onChange={setField}
                onBlur={validateField}
                type="textarea"
                maxLength={200}
                characterCounter
              />
              <EventTypeFields
                eventType={fields.eventType}
                eventTypes={eventTypes}
                locationType={fields.locationType}
                onEventTypeChange={setField}
                onEventTypeBlur={validateField}
                onLocationTypeChange={handleLocationTypeChange}
              />
              <EventListingsEventLocationInputs
                fields={fields}
                setField={setField}
                validateField={validateField}
                resetField={resetField}
                setValidators={setValidators}
              />
              <ControlledFormInput
                field={fields.mainUrl}
                fieldName="mainUrl"
                onChange={setField}
                onBlur={validateField}
                required
              />
              <div className="row">
                <div className="columns large-6">
                  <DatePicker
                    className={styles.eventDates}
                    labelText={fields.startDateTime.label}
                    name="startDateTime"
                    onChange={handleStartDateTimeChange('startDateTime', fields.endDate.value, fields.publishDate.value)}
                    onReady={handleDatepickerReady('startDateTime')}
                    placeholder={fields.startDateTime.placeholder}
                    required
                    value={fields.startDateTime.value}
                    config={{
                      // don't let the user select a datetime less than 5 minutes from now (in ET)
                      minDate: addMilliseconds(nowInETLocal, minTimeOffsetInMilliseconds),
                      // For post-selection display
                      altInput: true,
                      altFormat: 'm / d / Y, h:i K ET',
                    }}
                  />
                  <InputFieldErrorMessage field={fields.startDateTime} />
                </div>
                <div className="columns large-6">
                  <DatePicker
                    className={styles.eventDates}
                    config={dateOnlyConfig}
                    labelText={fields.endDate.label}
                    name="endDate"
                    onChange={handleEndDateChange('endDate', fields.startDateTime.value, fields.publishDate.value)}
                    onReady={handleDatepickerReady('endDate')}
                    placeholder={fields.endDate.placeholder}
                    required={false}
                    value={fields.endDate.value}
                  />
                  <InputFieldErrorMessage field={fields.endDate} />
                </div>
              </div>
              <ControlledFormInput
                field={fields.presentedBy}
                fieldName="presentedBy"
                onChange={setField}
                onBlur={validateField}
                required
              />
            </div>
          </div>
        </SubSection>
        <SubSection
          title="Upload event photo"
          description="PNG or JPEG under 10MB. Recommended image aspect ratio is 16:9."
        >
          <div className="row">
            <div className={`columns small-12 large-4 large-push-8 ${styles['event-photo-tips']}`}>
              <EventListingsQuickTips tips={eventPhotoTips} />
            </div>
            <div className="columns small-12 large-8 large-pull-4 end">
              <EventListingsPhotoUploader value={fields.photo.value} onChange={setField} />
              <div className={styles['publish-date-container']}>
                <EventListingsPublishDateField
                  field={fields.publishDate}
                  onChange={handlePublishDateChange('publishDate', fields.startDateTime.value, fields.endDate.value)}
                  onReady={handleDatepickerReady('publishDate')}
                  status={status}
                />
              </div>
              <EventListingsTermsField
                termsField={fields.terms}
                onChange={validateField}
              />
            </div>
          </div>
        </SubSection>
        <CreateScreenSubmissionButtons
          handleCheckout={handleCheckout}
          handleDelete={handleDelete}
          handlePreview={handlePreview}
          status={status}
          touched={isSubmittable}
        />
      </main>
    </>
  );
};

export default CreateScreen;
